import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { Ride } from '../../../../shared/interfaces/ride-data.interface';
import { RideNavigationService } from '../../../../shared/services/ride-navigation.service';
import { RidesService } from '../../../../shared/services/rides.service';
import { DriversService } from '../../../../shared/services/drivers.service';
import { GridDataService } from '../../../../shared/services/grid-data.service';
import { OverviewComponent } from '../../../telematics/overview/overview.component';
import { TelematicsMapComponent } from '../../../telematics/telematics-map/telematics-map.component';
import { TrackingComponent } from '../../../telematics/tracking/tracking.component';
import { TabComponent } from '../../../../shared/components/tabs/tab/tab.component';
import { TabsComponent } from '../../../../shared/components/tabs/tabs.component';
import { BaseComponent } from '../../../../shared/components/base.component';
import { DashboardService } from '../../services/dashboard.service';
import { GpsConnectivityComponent } from '../../../telematics/gps-connectivity/gps-connectivity.component';
import { AuthenticationService } from 'src/app/core/auth/services/authentication.service';

@Component({
  selector: 'app-dashboard-page',
  imports: [
    TabsComponent,
    TabComponent,
    OverviewComponent,
    TrackingComponent,
    TelematicsMapComponent,
    GpsConnectivityComponent,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [
    RideNavigationService,
    RidesService,
    DriversService,
    GridDataService,
    DatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPageComponent
  extends BaseComponent
  implements AfterViewInit
{
  public urlSafe = signal<string>('');
  public userData: any;
  public selectedTab: string = 'Overview';
  public rideData: Ride = new Ride();
  public currentPathIndex: number = 0;
  private isRestoringState = true; // Flag to prevent saving active tab during restoration
  @ViewChild(TabsComponent) tabsComponent!: TabsComponent;
  @ViewChild(TelematicsMapComponent)
  telematicsMapComponent!: TelematicsMapComponent;

  constructor(
    private readonly _dashboardService: DashboardService,
    private readonly cdr: ChangeDetectorRef,
    public authenticationService: AuthenticationService,
    private readonly router: Router,
    private readonly _rideNavigationService: RideNavigationService,
  ) {
    super();
    this.userData = this.authenticationService.currentUserValue;

    this._rideNavigationService.onChangeData.subscribe((result: Ride) => {
      this.rideData = result;

      // Trigger change detection to ensure the view updates
      this.cdr.detectChanges();

      // Switch to Telematics tab when ride data is received
      setTimeout(() => {
        this.tabsComponent.selectTab(2, false);
        this.cdr.detectChanges();
      }, 100);
    });
  }

  ngAfterViewInit() {
    // Restore the last active tab
    const savedTabIndex = this._dashboardService.getActiveTab();

    if (savedTabIndex > 0 && this.tabsComponent) {
      this.currentPathIndex = savedTabIndex;
      this.tabsComponent.selectTab(savedTabIndex, false);
    }

    // Allow tab changes to be saved after restoration is complete
    setTimeout(() => {
      this.isRestoringState = false;
    }, 200);
  }

  public onTabSelectionChange(data: any) {
    this.selectedTab = data.tab.title;
    this.currentPathIndex = data.index;

    // Only save the active tab index if we're not in the middle of restoring state
    if (!this.isRestoringState) {
      this._dashboardService.setActiveTab(data.index);
    }

    // If we're switching away from the telematics tab, clear all data
    if (data.tab.title !== 'Telematics' && this.telematicsMapComponent) {
      this.rideData = new Ride();
      this.telematicsMapComponent.Clear();
    }

    // Always clear rideData when switching away from any tab
    if (!data.tab.isActive) {
      this.rideData = new Ride();
    }

    // Force clear rideData specifically when leaving telematics tab
    if (data.tab.title !== 'Telematics') {
      this.rideData = new Ride();
    }
  }
}
